import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Auth } from '@freelancer/auth';
import * as Raven from 'raven-js';
import { map } from 'rxjs/operators';
import * as UAParser from 'ua-parser-js';
import { TRACKING_CONFIG } from './tracking.config';
import { TrackingConfig } from './tracking.interface';

export type ErrorOptions = Raven.RavenOptions;

@Injectable()
export class ErrorTracking {
  constructor(
    private auth: Auth,
    @Inject(TRACKING_CONFIG) private config: TrackingConfig,
    @Inject(PLATFORM_ID) platformId: Object,
  ) {
    if (
      isPlatformBrowser(platformId) &&
      this.config.enableErrorTracking &&
      this.isCurrentBrowser()
    ) {
      Raven.config(this.config.sentryDsn, {
        release: this.config.gitRevision,
        tags: {
          releaseDate: this.config.buildDate,
        },
        ignoreErrors: [
          /Response with status: 0\s+for URL: null/,
          'Http failure response for (unknown url): 0',
          /Error: Loading chunk \S+ failed/,
          'window.requestIdleCallback is not a function',
          "is not a function (evaluating 'window.requestIdleCallback",
          "Object doesn't support property or method 'requestIdleCallback'",
          'The system cannot locate the resource specified',
          'Cannot connect to server',
          'Invalid pointer',
          'Out of memory',
          'Not enough storage is available to complete this operation.',
          'setTimeout is not defined',
        ],
        ignoreUrls: [/^file:\/\//],
        dataCallback(data) {
          if (data.exception && data.exception.values) {
            const { frames } = data.exception.values[0].stacktrace;
            for (let i = frames.length - 1; i >= 0; i--) {
              const frame = frames[i];
              // consider that all non-chunk code isn't part of the app from
              // Sentry's grouping algorithm points of view, except if the
              // first frame isn't in a chunk, while making sure we always have
              // > 10% of in_app frames.
              if (
                i !== frames.length &&
                !frame.filename.endsWith('chunk.js') &&
                (frames.length - (i + 1)) / frames.length > 0.1
              ) {
                frame.in_app = false;
              }
            }
          }
          return data;
        },
      });
    }
  }

  captureMessage(msg: string, options?: ErrorOptions): void {
    if (this.config.enableErrorTracking && this.isCurrentBrowser()) {
      this.auth.authState
        .pipe(
          map(auth => {
            if (auth) {
              Raven.setUserContext({ id: auth.userId });
            }
            Raven.captureMessage(msg, options);
          }),
        )
        .toPromise();
    }
  }

  captureException(error: Error, options?: ErrorOptions): void {
    if (this.config.enableErrorTracking && this.isCurrentBrowser()) {
      this.auth.authState
        .pipe(
          map(auth => {
            if (auth) {
              Raven.setUserContext({ id: auth.userId });
            }
            Raven.captureException(error, options);
          }),
        )
        .toPromise();
    }
  }

  // yeah UA parsing is bad blah blah but catching large failures quickly is
  // more important than being flooded by exotic browsers.
  private isCurrentBrowser() {
    const ua = new UAParser(window.navigator.userAgent);
    const { name, version = '' } = ua.getBrowser();
    if (
      (name === 'IE' && parseInt(version, 10) === 11) ||
      (name === 'Edge' && parseInt(version, 10) >= 14) ||
      (name === 'Firefox' &&
        parseInt(version, 10) >= 52 &&
        !(ua.getOS().name === 'Android')) ||
      (name === 'Chrome' && parseInt(version, 10) >= 49) ||
      (name === 'Safari' && parseInt(version, 10) >= 10) ||
      (name === 'Opera' && parseInt(version, 10) >= 46)
    ) {
      return true;
    }
    return false;
  }
}
